import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiStaff } from '../../api/staffClient';
import TopBar from '../../components/TopBar';

type Respuesta = {
  id: number;
  mensaje: string;
  nuevo_estado: string | null;
  es_publica: boolean;
  creado_en: string;
  respondido_por: string | null;
};
type Imagen = { ruta: string; url: string };
type Reclamo = {
  id: number;
  nro_expediente: string;
  titulo: string;
  descripcion: string | null;
  estado: string;
  prioridad: string;
  creado_en: string;
  ciudadano: string;
  categoria: string | null;
  categoria_icono: string | null;
  area: string | null;
  respuestas: Respuesta[];
  imagenes: Imagen[];
};

const ESTADO_COLOR: Record<string, string> = {
  pendiente:  '#f59e0b',
  en_proceso: '#3b82f6',
  resuelto:   '#10b981',
  cerrado:    '#6b7280',
  rechazado:  '#ef4444',
};
const ESTADO_LABEL: Record<string, string> = {
  pendiente:  'Pendiente',
  en_proceso: 'En proceso',
  resuelto:   'Resuelto',
  cerrado:    'Cerrado',
  rechazado:  'Rechazado',
};
const ESTADOS_OPCIONES = [
  { value: 'pendiente',  label: 'Pendiente'  },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'resuelto',   label: 'Resuelto'   },
  { value: 'rechazado',  label: 'Rechazado'  },
];

function fmtFecha(iso: string) {
  return new Date(iso.replace(' ', 'T')).toLocaleString('es-AR', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function StaffDetalleReclamo() {
  const { id }                  = useParams();
  const [reclamo, setReclamo]   = useState<Reclamo | null>(null);
  const [err,     setErr]       = useState<string | null>(null);

  // Form de respuesta
  const [mensaje,   setMensaje]   = useState('');
  const [nuevoEst,  setNuevoEst]  = useState('');
  const [sending,   setSending]   = useState(false);
  const [sendErr,   setSendErr]   = useState<string | null>(null);
  const textareaRef               = useRef<HTMLTextAreaElement>(null);

  // Lightbox
  const [imgIdx,    setImgIdx]    = useState<number | null>(null);
  const touchStart                = useRef<number | null>(null);

  useEffect(() => {
    apiStaff<Reclamo>(`/staff?action=reclamo&id=${id}`)
      .then(setReclamo)
      .catch((e) => setErr(e.message));
  }, [id]);

  async function enviarRespuesta(e: React.FormEvent) {
    e.preventDefault();
    if (!mensaje.trim() && !nuevoEst) return setSendErr('Escribí un mensaje o elegí un nuevo estado');
    setSending(true);
    setSendErr(null);
    try {
      await apiStaff(`/staff?action=reclamo&id=${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          mensaje:  mensaje.trim() || undefined,
          estado:   nuevoEst || undefined,
        }),
      });
      // Recargar reclamo actualizado
      const updated = await apiStaff<Reclamo>(`/staff?action=reclamo&id=${id}`);
      setReclamo(updated);
      setMensaje('');
      setNuevoEst('');
    } catch (e: any) {
      setSendErr(e.message || 'Error al enviar');
    } finally {
      setSending(false);
    }
  }

  const esFinal = reclamo && ['cerrado', 'rechazado'].includes(reclamo.estado);

  if (err) return (
    <>
      <TopBar title="Reclamo" back />
      <main className="px-4 py-4">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{err}</div>
      </main>
    </>
  );
  if (!reclamo) return (
    <>
      <TopBar title="Reclamo" back />
      <main className="px-4 pt-10">
        <p className="text-center text-gray-400">Cargando…</p>
      </main>
    </>
  );

  const color = ESTADO_COLOR[reclamo.estado] ?? '#6b7280';

  return (
    <>
      <TopBar title={`Exp. ${reclamo.nro_expediente}`} back />

      {/* Lightbox fotos */}
      {imgIdx !== null && reclamo.imagenes.length > 0 && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.95)' }}
          onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchStart.current === null) return;
            const dx = e.changedTouches[0].clientX - touchStart.current;
            if (dx > 50)  setImgIdx((p) => Math.max(0, (p ?? 0) - 1));
            if (dx < -50) setImgIdx((p) => Math.min(reclamo.imagenes.length - 1, (p ?? 0) + 1));
          }}
          onClick={() => setImgIdx(null)}
        >
          <img
            src={reclamo.imagenes[imgIdx].url}
            alt=""
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
          />
        </div>
      )}

      <main className="max-w-md mx-auto px-4 py-4 pb-6 space-y-4">

        {/* Encabezado */}
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span
              className="text-xs font-bold px-2.5 py-0.5 rounded-full"
              style={{ backgroundColor: color + '20', color }}
            >
              {ESTADO_LABEL[reclamo.estado] ?? reclamo.estado}
            </span>
            {reclamo.prioridad && (
              <span className="text-xs text-gray-400 font-medium">
                Prioridad: {reclamo.prioridad}
              </span>
            )}
          </div>
          <h1 className="text-lg font-extrabold text-gray-900 leading-snug">{reclamo.titulo}</h1>
          {reclamo.categoria && (
            <p className="text-sm text-gray-500 mt-0.5">
              {reclamo.categoria_icono} {reclamo.categoria}
            </p>
          )}
        </div>

        {/* Metadatos */}
        <div className="bg-white border border-app rounded-xl p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-400">Ciudadano</span>
            <span className="font-semibold text-gray-800">{reclamo.ciudadano}</span>
          </div>
          {reclamo.area && (
            <div className="flex justify-between">
              <span className="text-gray-400">Área</span>
              <span className="font-semibold text-gray-800">{reclamo.area}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-gray-400">Ingresado</span>
            <span className="font-semibold text-gray-800">{fmtFecha(reclamo.creado_en)}</span>
          </div>
        </div>

        {/* Descripción */}
        {reclamo.descripcion && (
          <div className="bg-white border border-app rounded-xl p-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Descripción</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{reclamo.descripcion}</p>
          </div>
        )}

        {/* Fotos */}
        {reclamo.imagenes.length > 0 && (
          <div className="bg-white border border-app rounded-xl p-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Fotos</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {reclamo.imagenes.map((img, i) => (
                <button key={i} onClick={() => setImgIdx(i)} className="flex-shrink-0 active:scale-95 transition-transform">
                  <img
                    src={img.url}
                    alt=""
                    className="w-28 h-20 rounded-xl object-cover border border-app bg-gray-100"
                    onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Historial de respuestas */}
        {reclamo.respuestas.length > 0 && (
          <div className="bg-white border border-app rounded-xl p-4 space-y-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Historial</p>
            {reclamo.respuestas.map((r) => (
              <div key={r.id} className="pl-3" style={{ borderLeft: '3px solid #e5e7eb' }}>
                {r.nuevo_estado && (
                  <span
                    className="inline-block text-xs font-bold px-2 py-0.5 rounded-full mb-1"
                    style={{
                      backgroundColor: (ESTADO_COLOR[r.nuevo_estado] ?? '#6b7280') + '20',
                      color: ESTADO_COLOR[r.nuevo_estado] ?? '#6b7280',
                    }}
                  >
                    → {ESTADO_LABEL[r.nuevo_estado] ?? r.nuevo_estado}
                  </span>
                )}
                {r.mensaje && (
                  <p className="text-sm text-gray-700 leading-relaxed">{r.mensaje}</p>
                )}
                <p className="text-xs text-gray-300 mt-1">
                  {r.respondido_por ?? 'Personal municipal'} · {fmtFecha(r.creado_en)}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Form de respuesta */}
        <div className="bg-white border border-app rounded-xl p-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
            {esFinal ? 'Reclamo cerrado' : 'Responder'}
          </p>

          {esFinal ? (
            <p className="text-sm text-gray-400">
              Este reclamo está en estado final y no puede modificarse.
            </p>
          ) : (
            <form onSubmit={enviarRespuesta} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: '#64748b' }}>
                  Mensaje para el ciudadano (opcional)
                </label>
                <textarea
                  ref={textareaRef}
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  rows={3}
                  placeholder="Escribí una actualización…"
                  className="w-full rounded-xl px-4 py-3 text-sm outline-none resize-none"
                  style={{ backgroundColor: '#f8fafc', border: '1px solid #6e6b6b', color: '#303030' }}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: '#64748b' }}>
                  Cambiar estado (opcional)
                </label>
                <select
                  value={nuevoEst}
                  onChange={(e) => setNuevoEst(e.target.value)}
                  className="w-full rounded-xl px-4 py-3 text-sm outline-none"
                  style={{ backgroundColor: '#f8fafc', border: '1px solid #6e6b6b', color: '#303030' }}
                >
                  <option value="">— Sin cambio —</option>
                  {ESTADOS_OPCIONES.filter((o) => o.value !== reclamo.estado).map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {sendErr && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{sendErr}</div>
              )}

              <button
                type="submit"
                disabled={sending}
                className="w-full bg-brand-600 hover:bg-brand-700 rounded-xl py-3.5 font-bold text-sm text-white disabled:opacity-50"
              >
                {sending ? 'Enviando…' : 'Enviar respuesta'}
              </button>
            </form>
          )}
        </div>

      </main>
    </>
  );
}
